import { AppText } from "@toothfairy/shared-ui";
import AppTheme from "@toothfairy/shared-ui/AppTheme";
import AppStateManager from "@toothfairy/shared-ui/AppStateManager";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { StyleSheet, View } from "react-native";
import finetuningHelper from "../../API/FineTuning/finetuningHelper";

// Resolve the Training Log id behind a fine-tuned model id — the serving
// endpoints are keyed by training log id, while dropdowns/agents reference
// the model id. New pipeline models use the registry id (ToothFairyAI/…,
// mirrored into trainingMeta as tuned_model_id); legacy completions the raw
// model_id / t_model_id.
export const findTrainingLogIdForModel = (trainingLogs, modelId) => {
  if (!modelId) return null;
  const match = trainingLogs?.find((u) => {
    if (u?.status !== "completed" || !u?.trainingMeta) return false;
    let tm = u?.trainingMeta;
    if (typeof tm === "string") {
      try {
        tm = JSON.parse(tm);
      } catch {
        return false;
      }
    }
    return (
      tm?.tuned_model_id === modelId ||
      tm?.model_id === modelId ||
      tm?.t_model_id === modelId
    );
  });
  return match?.id || null;
};

// Live serving-lane status pill for a fine-tuned ("custom") base model.
// Renders nothing when the model id is not a fine-tuned model of this
// workspace, or when the serving state is temporarily unavailable.
const FineTunedModelStatusChip = ({ modelId, token }) => {
  const { t } = useTranslation();
  const { currentTheme } = AppTheme.useTheme();
  const { state } = AppStateManager.useAppStateManager();

  const [serving, setServing] = useState(null);
  const [resolved, setResolved] = useState(false);

  const trainingLogId = useMemo(
    () => findTrainingLogIdForModel(state?.trainingLogs, modelId),
    [JSON.stringify(state?.trainingLogs), modelId]
  );

  useEffect(() => {
    if (!trainingLogId || !token) {
      setResolved(false);
      return;
    }
    let cancelled = false;
    finetuningHelper
      .getServing({ token, trainingLogId })
      .then((res) => {
        if (!cancelled) {
          setServing(res);
          setResolved(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setServing(null);
          setResolved(true);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [trainingLogId, token]);

  // A fine-tuned model must always surface its lane state — including when
  // there is no serving record at all (never configured / fetch failed):
  // that state IS "not served" and the user needs to see it.
  if (!trainingLogId || !resolved) return null;

  const laneStatus = serving?.lane?.status || "stopped";
  const mode = serving?.mode;

  const label =
    laneStatus === "ready"
      ? t("training.serving.chipRunning", "Running")
      : laneStatus === "starting" || laneStatus === "creating"
      ? t("training.serving.chipWaking", "Waking up")
      : t("training.serving.chipAsleep", "Asleep");
  const dotColor =
    laneStatus === "ready"
      ? "#2A9D8F"
      : laneStatus === "starting" || laneStatus === "creating"
      ? "#F4A261"
      : "#888888";

  return (
    <View
      style={{
        alignSelf: "flex-start",
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        marginLeft: 15,
      }}
    >
      <View style={[styles.dot, { backgroundColor: dotColor }]} />
      <AppText style={styles.chip} color={currentTheme?.theme?.font_color}>
        {label}
        {mode === "scheduled"
          ? ` · ${t("training.serving.modeScheduled", "scheduled")}`
          : mode === "always"
          ? ` · ${t("training.serving.modeAlways", "always")}`
          : ""}
      </AppText>
    </View>
  );
};

const styles = StyleSheet.create({
  chip: {
    fontSize: 12,
    fontWeight: "600",
  },
  dot: {
    width: 8,
    height: 8,
    borderRadius: 4,
  },
});

export default FineTunedModelStatusChip;
